import dayjs from 'dayjs';

const INSET_Y = 4;
const MIN_EXTRA = 8;

const sameDay = (a, b) => dayjs(a.date).startOf('day').isSame(dayjs(b.date).startOf('day'));

const rowStart = (minutes) => Math.floor(minutes / 60) * 60;

// Тянем карточку до finishMin, только если по пути нет другой карточки того же дня.
// Обычные карточки всегда вверху своей часовой ячейки, поэтому при окончании
// не на границе часа любая карточка в этой ячейке тоже считается «на пути».
export function collectStretchableIds(items) {
    const blocked = new Set();

    items.forEach((item) => {
        if (!(item.finishMin > item.startMin)) {
            blocked.add(item.id);
            return;
        }

        const finishRow = rowStart(item.finishMin);
        const endsMidHour = item.finishMin !== finishRow;

        const hasItemOnPath = items.some((other) => {
            if (other.id === item.id || !sameDay(other, item)) return false;
            if (other.startMin >= item.startMin && other.startMin < item.finishMin) return true;
            return endsMidHour && rowStart(other.startMin) === finishRow;
        });

        if (hasItemOnPath) blocked.add(item.id);
    });

    // Если в том же часе сосед не тянется (обычная карточка), остальных тоже не тянем —
    // иначе абсолютный оверлей наложится на них.
    let changed = true;
    while (changed) {
        changed = false;
        items.forEach((item) => {
            if (blocked.has(item.id)) return;
            const blockedNeighbor = items.some(
                (other) =>
                    other.id !== item.id &&
                    blocked.has(other.id) &&
                    sameDay(other, item) &&
                    rowStart(other.startMin) === rowStart(item.startMin)
            );
            if (blockedNeighbor) {
                blocked.add(item.id);
                changed = true;
            }
        });
    }

    return new Set(items.filter((item) => !blocked.has(item.id)).map((item) => item.id));
}

function calendarRows(root) {
    return [...root.querySelectorAll('.week-calendar-row[data-start-min]')];
}

function rowByMinute(rows, minute) {
    return rows.find((row) => {
        const start = Number(row.dataset.startMin);
        return minute >= start && minute < start + 60;
    });
}

function cellOf(row, day) {
    if (!row) return null;
    return row.querySelector(`[data-day="${day}"]`);
}

function endAnchor(rows, finishMin, day) {
    const boundary = rows.find((row) => Number(row.dataset.startMin) === finishMin);
    const boundaryCell = cellOf(boundary, day);
    if (boundaryCell) return { cell: boundaryCell, frac: 0 };

    const row = rowByMinute(rows, finishMin);
    const rowCell = cellOf(row, day);
    if (rowCell) {
        const start = Number(row.dataset.startMin);
        return { cell: rowCell, frac: (finishMin - start) / 60 };
    }

    const last = rows[rows.length - 1];
    const lastCell = cellOf(last, day);
    if (!lastCell) return null;
    const lastStart = Number(last.dataset.startMin);
    if (finishMin >= lastStart + 60) return { cell: lastCell, frac: 1 };
    if (finishMin > lastStart) return { cell: lastCell, frac: (finishMin - lastStart) / 60 };
    return null;
}

function pointY(cell, frac) {
    return cell.getBoundingClientRect().top + cell.clientTop + frac * cell.clientHeight;
}

export function stretchFrame(root, el) {
    const startMin = Number(el.dataset.startMin);
    const finishMin = Number(el.dataset.finishMin);
    const day = el.dataset.day;
    if (!Number.isFinite(startMin) || !Number.isFinite(finishMin) || finishMin <= startMin || day == null) return null;

    const rows = calendarRows(root);
    const startRow = rowByMinute(rows, startMin);
    const startCell = cellOf(startRow, day);
    const end = endAnchor(rows, finishMin, day);
    if (!startCell || !end?.cell || startCell.clientHeight <= 0) return null;

    const style = getComputedStyle(startCell);
    const padLeft = parseFloat(style.paddingLeft) || 0;
    const padRight = parseFloat(style.paddingRight) || 0;
    const width = startCell.clientWidth - padLeft - padRight;
    if (width <= 0) return null;

    const startFrac = (startMin - Number(startRow.dataset.startMin)) / 60;
    const yStart = pointY(startCell, startFrac);
    const yEnd = pointY(end.cell, end.frac);
    const originTop = root.getBoundingClientRect().top + root.clientTop;
    const originLeft = root.getBoundingClientRect().left + root.clientLeft;
    const top = yStart - originTop + INSET_Y;
    const height = yEnd - yStart - INSET_Y * 2;
    const left = startCell.getBoundingClientRect().left + startCell.clientLeft + padLeft - originLeft;

    if (height <= 0) return null;

    return {
        top: Math.round(top),
        left: Math.round(left),
        width: Math.round(width),
        height: Math.round(height),
    };
}

function rowStartMinute(root, el) {
    const startMin = Number(el.dataset.startMin);
    const row = rowByMinute(calendarRows(root), startMin);
    return row ? Number(row.dataset.startMin) : null;
}

function contentWidth(root, el) {
    const row = rowByMinute(calendarRows(root), Number(el.dataset.startMin));
    const cell = cellOf(row, el.dataset.day);
    if (!cell) return null;
    const style = getComputedStyle(cell);
    const padLeft = parseFloat(style.paddingLeft) || 0;
    const padRight = parseFloat(style.paddingRight) || 0;
    return cell.clientWidth - padLeft - padRight;
}

export function measureStretchLayouts(root) {
    const candidates = [...root.querySelectorAll('[data-stretch-candidate]')];
    if (candidates.length === 0) return {};

    const snapshots = candidates.map((el) => ({ el, cssText: el.style.cssText }));
    const rejected = new Set();

    const place = (el, inFlow) => {
        if (inFlow) {
            el.style.position = 'static';
            el.style.visibility = 'hidden';
            el.style.height = 'auto';
            el.style.width = 'auto';
            el.style.margin = '';
            el.style.left = '';
            el.style.top = '';
            el.style.right = '';
            el.style.boxSizing = 'border-box';
            return;
        }

        const width = contentWidth(root, el);
        el.style.position = 'absolute';
        el.style.visibility = 'hidden';
        el.style.height = 'auto';
        el.style.width = width == null ? 'auto' : `${Math.max(width, 0)}px`;
        el.style.margin = '0';
        el.style.left = '0';
        el.style.top = '0';
        el.style.right = 'auto';
        el.style.boxSizing = 'border-box';
    };

    try {
        let layouts = {};
        const maxPasses = candidates.length + 1;

        for (let pass = 0; pass < maxPasses; pass++) {
            candidates.forEach((el) => place(el, rejected.has(el.dataset.itemId)));
            void root.offsetHeight;

            const nextRejected = new Set(rejected);
            const nextLayouts = {};

            candidates.forEach((el) => {
                const id = el.dataset.itemId;
                if (rejected.has(id)) return;

                const contentHeight = el.offsetHeight;
                const geom = stretchFrame(root, el);
                if (geom && geom.height >= contentHeight + MIN_EXTRA) nextLayouts[id] = geom;
                else nextRejected.add(id);
            });

            candidates.forEach((el) => {
                const id = el.dataset.itemId;
                if (nextRejected.has(id) || !nextLayouts[id]) return;
                const rowStart = rowStartMinute(root, el);
                const sharesCell = candidates.some((other) => {
                    if (other === el || !nextRejected.has(other.dataset.itemId)) return false;
                    return other.dataset.day === el.dataset.day && rowStartMinute(root, other) === rowStart;
                });
                if (sharesCell) {
                    nextRejected.add(id);
                    delete nextLayouts[id];
                }
            });

            const changed = nextRejected.size !== rejected.size;
            rejected.clear();
            nextRejected.forEach((id) => rejected.add(id));
            layouts = nextLayouts;
            if (!changed) return layouts;
        }

        return layouts;
    } finally {
        snapshots.forEach(({ el, cssText }) => {
            el.style.cssText = cssText;
        });
    }
}

export function recomputeFrames(root, layouts) {
    const next = {};
    let changed = false;

    Object.entries(layouts).forEach(([id, frame]) => {
        const el = root.querySelector(`[data-item-id="${CSS.escape(id)}"]`);
        const geom = el ? stretchFrame(root, el) : null;
        if (
            !geom ||
            (Math.abs(geom.top - frame.top) <= 1 &&
                Math.abs(geom.left - frame.left) <= 1 &&
                Math.abs(geom.width - frame.width) <= 1 &&
                Math.abs(geom.height - frame.height) <= 1)
        ) {
            next[id] = frame;
            return;
        }
        next[id] = geom;
        changed = true;
    });

    return changed ? next : layouts;
}
